import { useEffect } from 'react';
import indexHtml from '../../index.html?raw';

/**
 * The title and description the site falls back to, taken from index.html's source at build time.
 *
 * <p>Not from one place by hand: the static tags in index.html are what a crawler sees on any route
 * the backend doesn't render, and a second copy here would drift from them silently.
 *
 * <p>Not from the live document either, which is how this used to work. The backend replaces those
 * tags on event pages and the homepage before the script runs, so reading `document.title` at
 * startup captured *that page's* title as the site default. Land on an event, click through to
 * "Submit an event", and the tab went on naming the event.
 */
export const DEFAULTS = readDefaults(indexHtml);

export function readDefaults(html: string): { title: string; description: string } {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return {
    title: doc.querySelector('title')?.textContent?.trim() ?? '',
    description: doc.querySelector('meta[name="description"]')?.getAttribute('content') ?? '',
  };
}

function setDescription(content: string) {
  let tag = document.querySelector('meta[name="description"]');
  if (!tag) {
    // index.html ships one, so this only runs if that were ever removed. Creating it beats
    // silently doing nothing, which would be invisible until someone checked a search result.
    tag = document.createElement('meta');
    tag.setAttribute('name', 'description');
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

/**
 * Gives a page its own title and description.
 *
 * <p><b>Why this exists.</b> Every route served the same two tags: "Muslim Marriage Events" and one
 * generic sentence. In a browser tab that is merely unhelpful; in a search index it is the reason
 * pages sit in "crawled, currently not indexed" — Google fetches two dozen pages, sees the same
 * title and description on all of them, concludes it already has this page, and stores one.
 *
 * <p><b>Why not React 19's document metadata.</b> React 19 can render {@code <title>} in a
 * component and hoist it into the head, which is tidier. It does not deduplicate against the
 * static tag already in index.html, so the document would end up with two — and a browser uses
 * the first, which is the static one. Removing the static tag isn't an option either: it is what
 * anything that doesn't execute JavaScript reads. Setting the existing tags imperatively sidesteps
 * both problems.
 *
 * <p>Restores the site defaults on unmount, so a route that sets nothing doesn't inherit the last
 * event's title — which would be worse than the shared title it replaced.
 */
export function usePageMetadata({ title, description }: { title?: string; description?: string }) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) setDescription(description);

    return () => {
      document.title = DEFAULTS.title;
      setDescription(DEFAULTS.description);
    };
  }, [title, description]);
}
